import {
  theDomWatcher,
  handleNewsletterSubscribed,
  trackNewsletterSignup,
  initProductDescriptions,
} from "@theme/helpers";

/** Resolves after the load event, in idle time. */
const pageIdle = new Promise((resolve) => {
  const whenIdle = () =>
    window.requestIdleCallback
      ? window.requestIdleCallback(() => resolve(), { timeout: 3000 })
      : setTimeout(resolve, 200);
  if (document.readyState === "complete") whenIdle();
  else window.addEventListener("load", whenIdle, { once: true });
});

/**
 * Runs `loader` once the page is idle, or as soon as the shopper presses one of
 * `triggers`. A click that lands before the module is ready is held, then
 * replayed on its trigger.
 */
function loadWhenIdle(loader, triggers) {
  const selector = triggers.join(", ");
  let ready = false;
  let pending;

  const early = (event) => {
    if (event.target.closest?.(selector)) load();
  };
  const hold = (event) => {
    const trigger = event.target.closest?.(selector);
    if (!trigger || ready) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    load().then(() => trigger.click());
  };
  const load = () => {
    pending ??= Promise.resolve()
      .then(loader)
      .finally(() => {
        ready = true;
        document.removeEventListener("pointerdown", early, true);
        document.removeEventListener("click", hold, true);
      });
    return pending;
  };

  document.addEventListener("pointerdown", early, { capture: true, passive: true });
  document.addEventListener("click", hold, true);
  return pageIdle.then(load);
}

const featureLoaders = [
  {
    label: "cart",
    selectors: ["[data-cart-drawer]", "[data-cart-container]", "[data-open-cart]"],
    observe: true,
    loader: () => import("@theme/cart"),
  },
  {
    label: "wishlist",
    selectors: ["[wishlist-button]", "[wishlist-grid]", "[side-cart-wishlist-grid]"],
    observe: true,
    // Straight away on the wishlist page, where the grid is the content
    loader: () =>
      document.querySelector("[wishlist-grid]")
        ? import("@theme/wishlist")
        : loadWhenIdle(
            () => import("@theme/wishlist"),
            ["[wishlist-button]", '[data-sidecart-tab="wishlist"]:not(body)'],
          ),
  },
  {
    label: "side-cart-tabs",
    selectors: ['.side-cart-tabs[role="tablist"]', "[data-cart-tab]"],
    observe: true,
    loader: () => import("@theme/tabs"),
  },
  {
    label: "toasty",
    selectors: ["body"],
    loader: () => import("@theme/toasty"),
  },
  {
    label: "product-quick-add",
    selectors: ["[quick-add-qf]", "product-form"],
    observe: true,
    loader: () =>
      import("@theme/product-quick-add").then(({ ProductQuickAdd }) => new ProductQuickAdd()),
  },
  {
    label: "quickview",
    selectors: ["product-quick-view", "quickview-modal"],
    loader: () =>
      loadWhenIdle(
        () => import("@theme/quickview").then(({ ProductQuickView }) => new ProductQuickView()),
        ["product-quick-view"],
      ),
  },
  {
    label: "main-product",
    selectors: ["main-product"],
    observe: true,
    loader: () => import("@theme/main-product"),
  },
  {
    label: "product-gallery-grid",
    selectors: ['[gallery-container][data-gallery-style="grid"]'],
    loader: () => import("@theme/product-gallery"),
  },
  // {
  //   label: "product-form",
  //   selectors: ["product-form"],
  //   observe: true,
  //   loader: () => import("@theme/option-selectors").then(({ ProductForm }) => new ProductForm()),
  // },
  {
    label: "product-card",
    selectors: ["product-card"],
    loader: () => import("@theme/product-card").then(({ ProductCard }) => new ProductCard()),
  },
  {
    label: "custom-option-dropdown",
    selectors: ["custom-option-dropdown"],
    observe: true,
    loader: () => import("@theme/custom-option-dropdown"),
  },
  {
    label: "predictive-search",
    selectors: ["predictive-search"],
    loader: () => import("@theme/search").then(({ SearchController }) => new SearchController()),
  },
  {
    label: "menu",
    selectors: ["#open-mobile-menu", ".mobile-menu-outer-wrapper"],
    loader: () => import("@theme/menu").then(({ MenuController }) => new MenuController()),
  },
  {
    label: "menu-underline",
    selectors: ["[data-menu-underline]"],
    observe: true,
    loader: () => import("@theme/menu-underline"),
  },
  {
    label: "product-essentials",
    // The custom element is only in the PDP essentials section, but the module
    // also carries the delegated card quantity steppers - which ride along on
    // every essentials card, in the side cart's recommendations modal and the
    // cart page upsell as much as the PDP. Watching for the stepper too is what
    // loads the module where the element never appears.
    selectors: ["product-essentials-add", "[data-essentials-qty]"],
    observe: true,
    loader: () => import("@theme/product-essentials"),
  },
  {
    label: "faq-search",
    selectors: ["#fuse-search"],
    observe: true,
    loader: () => import("@theme/faq-search").then(({ initFAQSearch }) => initFAQSearch()),
  },
  {
    label: "accessory-quantity",
    selectors: ["[data-accessory-calculator]"],
    observe: true,
    loader: () =>
      import("@theme/accessory-quantity").then(({ initAccessoryQuantity }) =>
        initAccessoryQuantity(document),
      ),
  },
  {
    label: "product-colours",
    selectors: ["product-colours"],
    observe: true,
    loader: () => import("@theme/product-colours"),
  },
  {
    label: "roomvo",
    selectors: ["[data-roomvo-trigger]"],
    observe: true,
    loader: () => import("@theme/roomvo").then(({ initRoomvo }) => initRoomvo()),
  },
  {
    label: "product-calculator",
    selectors: ["[data-product-calculator]"],
    observe: true,
    loader: () =>
      import("@theme/product-calculator").then(({ initProductCalculator }) =>
        initProductCalculator(document),
      ),
  },
  {
    label: "gallery-dots",
    selectors: ["[data-gallery-dots]"],
    observe: true,
    loader: () =>
      import("@theme/gallery-dots").then(({ initGalleryDots }) => initGalleryDots(document)),
  },
  {
    label: "hero-thumbnails",
    selectors: ["[data-hero-thumb]"],
    loader: () =>
      import("@theme/hero-thumbnails").then(({ initHeroThumbnails }) =>
        initHeroThumbnails(document),
      ),
  },
  {
    label: "slider-active-slide",
    selectors: [
      '[data-slider-effect="fade"]',
      ".blaze-slider--centered-mobile",
      ".blaze-slider--centered-desktop",
    ],
    observe: true,
    loader: () =>
      import("@theme/slider-active-slide").then(({ initActiveSlide }) => initActiveSlide(document)),
  },
  {
    label: "copy-code",
    selectors: ["[data-copy-code]"],
    observe: true,
    loader: () => import("@theme/copy-code").then(({ initCopyCode }) => initCopyCode(document)),
  },
  {
    label: "graphene-card-extras",
    selectors: ["[data-graphene-results]", "[data-graphene-autocomplete]", "[data-graphene-recs]"],
    observe: true,
    loader: () =>
      import("@theme/graphene-card-extras").then(({ initCardExtras }) => initCardExtras()),
  },
  {
    label: "drag-scroll",
    selectors: ["[data-drag-scroll]"],
    observe: true,
    loader: () => import("@theme/drag-scroll").then(({ initDragScroll }) => initDragScroll()),
  },
  {
    label: "countdown",
    selectors: ["[data-countdown]"],
    observe: true,
    loader: () => import("@theme/countdown").then(({ initCountdown }) => initCountdown(document)),
  },
  {
    label: "expanding-panels",
    selectors: ["[data-expanding-panels]"],
    observe: true,
    loader: () =>
      import("@theme/expanding-panels").then(({ initExpandingPanels }) =>
        initExpandingPanels(document),
      ),
  },
  {
    label: "floor-quiz",
    selectors: ["[data-floor-quiz]"],
    observe: true,
    loader: () => import("@theme/floor-quiz").then(({ initFloorQuiz }) => initFloorQuiz(document)),
  },
  {
    label: "collection-empty-state",
    selectors: ["[data-collection-empty-state]"],
    loader: () =>
      import("@theme/collection-empty-state").then(
        ({ CollectionEmptyState }) => new CollectionEmptyState(),
      ),
  },
  {
    label: "sample-box",
    selectors: ["[data-sample-add]"],
    observe: true,
    // initSampleBox re-reads /cart.js, so it waits for idle unless a sample button is pressed
    loader: () =>
      loadWhenIdle(
        () => import("@theme/sample-box").then(({ initSampleBox }) => initSampleBox(document)),
        ["[data-sample-add]", '[data-sidecart-tab="samples"]:not(body)'],
      ),
  },
  {
    label: "trustpilot",
    // .tp-widget is on every widget from the start; .trustpilot-widget is added
    // by trustpilot.js as each one nears the viewport (and is already there on
    // any widget rendered by something other than the snippet).
    selectors: [".tp-widget", ".trustpilot-widget"],
    observe: true,
    loader: () => import("@theme/trustpilot").then(({ initTrustpilot }) => initTrustpilot()),
  },
  {
    label: "active-slide-video",
    selectors: ["[data-active-slide-video]"],
    observe: true,
    loader: () =>
      import("@theme/active-slide-video").then(({ initActiveSlideVideo }) =>
        initActiveSlideVideo(document),
      ),
  },
  {
    // Videos render their sources as data-src and rely on this module to promote
    // them once in view. Without it a lazy video never loads at all.
    label: "video-controls",
    selectors: ["[js-video-controls]"],
    observe: true,
    loader: () => import("@theme/video-controls"),
  },
  {
    label: "columns-tabs",
    selectors: ["[data-columns-tabs]"],
    observe: true,
    loader: () =>
      import("@theme/columns-tabs").then(({ initColumnsTabs }) => initColumnsTabs(document)),
  },
  {
    label: "filter-drawer",
    selectors: ["[data-filter-drawer]"],
    observe: true,
    loader: () =>
      import("@theme/filter-drawer").then(({ initFilterDrawer }) => initFilterDrawer(document)),
  },
  {
    label: "slider-tabs",
    selectors: ["[data-slider-tabs]"],
    observe: true,
    loader: () =>
      import("@theme/slider-tabs").then(({ initSliderTabs }) => initSliderTabs(document)),
  },
  {
    label: "inspiration",
    selectors: ["[data-inspiration]"],
    observe: true,
    loader: () =>
      import("@theme/inspiration").then(({ initInspiration }) => initInspiration(document)),
  },
  {
    label: "article-inspiration",
    selectors: ["[data-article-inspiration]"],
    observe: true,
    loader: () =>
      import("@theme/article-inspiration").then(({ initArticleInspiration }) =>
        initArticleInspiration(document),
      ),
  },
  {
    label: "announcement-bar",
    selectors: ["[data-announcement-bar]"],
    loader: () =>
      import("@theme/announcement-bar").then(({ AnnouncementBar }) => new AnnouncementBar()),
  },
  {
    label: "accordion",
    selectors: [".accordion"],
    loader: () =>
      import("@theme/accordion").then(({ AccordionController }) => new AccordionController()),
  },
  {
    label: "product-quantity",
    selectors: ["[quantity-select]"],
    observe: true,
    loader: () =>
      import("@theme/product-quantity").then(({ ProductQuantity }) => new ProductQuantity()),
  },
  {
    label: "header-layout",
    selectors: ["[data-header-layout]"],
    loader: () =>
      import("@theme/header-layout").then(
        ({ HeaderLayoutController }) => new HeaderLayoutController(),
      ),
  },
  {
    label: "header-scroll",
    selectors: [".header-outer-shopify-wrapper"],
    loader: () =>
      import("@theme/header-scroll").then(
        ({ HeaderScrollController }) => new HeaderScrollController(),
      ),
  },
  {
    label: "modal-controller",
    selectors: ["[data-modal-trigger]", "[data-modal]"],
    loader: () =>
      import("@theme/modal-controller").then(({ ModalController }) => {
        if (window.__quickfireModalController) {
          return window.__quickfireModalController;
        }
        const instance = new ModalController();
        window.__quickfireModalController = instance;
        return instance;
      }),
  },
  {
    label: "lightbox",
    selectors: ["[data-lightbox-modal]"],
    observe: true,
    loader: () => import("@theme/lightbox").then(({ bootstrapLightbox }) => bootstrapLightbox()),
  },
  {
    label: "slider-mobile-grid",
    selectors: ["[data-mobile-grid-slider]"],
    observe: true,
    loader: () => import("@theme/slider-mobile-grid"),
  },
];

featureLoaders.forEach(({ selectors, loader, label, observe }) =>
  theDomWatcher(selectors, loader, { label, observe }),
);

theDomWatcher(
  "[data-blaze-slider]",
  () =>
    import("@theme/blaze-slider-controller").then(({ blazeSliderController }) => {
      blazeSliderController.init(document);
      return blazeSliderController;
    }),
  { label: "blaze-slider", observe: true },
);

theDomWatcher("form.newsletter-form", handleNewsletterSubscribed, { label: "newsletter-form" });
theDomWatcher('input[name="contact[email]"]', trackNewsletterSignup, {
  label: "newsletter-signup",
});
theDomWatcher("[read-more-container]", initProductDescriptions, { label: "read-more" });
